import {Button, ButtonProps, Stack, StackProps, ToggleButtonGroup, ToggleButtonGroupProps, useTheme} from "@mui/joy";
import {PropsWithChildren, useCallback} from "react";
import {IconDefinition} from "@fortawesome/pro-regular-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {IconProp} from "@fortawesome/fontawesome-svg-core";

import {mergeSx} from "../components/joyx.tsx";

export const toggleIconProps = {
    size: "2x",
} as const;

export const toggleIconCounterSx = {
    transform: "scale(0.8) translate(28px, -16px)",
    backgroundColor: "tomato",
    borderRadius: "12px",
    color: "white",
} as const;

export function ToggleBar({children, sx: inSx, ...rest}: PropsWithChildren<Omit<StackProps, "direction">>) {
    const theme = useTheme();
    const sx = mergeSx({
        width: "100%",
        backgroundColor: theme.palette.primary[50],
        justifyContent: "space-evenly"
    }, inSx);
    return <Stack direction="row" sx={sx} {...rest}>{children}</Stack>;
}

export interface ToggleSwitchProps<T extends string> extends Omit<ToggleButtonGroupProps, "children"> {
    value: T;
    dispatchValue: (next: T) => void;
    disabled?: boolean;
}

export interface IconToggleSwitchProps<T extends string> extends ToggleSwitchProps<T> {
    icons: Record<T, IconProp>;
}

export const toggleSwitchSx = {px: 2, py: 1.2} as const;
export const toggleSwitchButtonSx = {px: 3, py: 1.2} as const;

export function IconToggleSwitch<T extends string>({
                                                       value,
                                                       dispatchValue,
                                                       icons,
                                                       color: inColor,
                                                       variant: inVariant,
                                                       ...rest
                                                   }: IconToggleSwitchProps<T>) {
    const color = inColor ?? "primary";
    const variant = inVariant ?? "soft";
    return <ToggleButtonGroup<T> value={value} onChange={(_, next) => {
        if (!next || !(next in icons)) {
            throw new Error("unexpected null");
        }
        dispatchValue(next as T);
    }} color={color} variant={variant} sx={toggleSwitchSx} buttonFlex={1} {...rest}>
        {(Object.keys(icons) as T[]).map(k => {
            return <Button sx={toggleSwitchButtonSx} value={k} key={k}>
                <FontAwesomeIcon {...toggleIconProps} icon={icons[k]}/>
            </Button>
        })}
    </ToggleButtonGroup>
}

export interface ToggleBarIconProps {
    icon: IconDefinition;
}

export function ToggleBarIcon({icon}: ToggleBarIconProps) {
    return <FontAwesomeIcon {...toggleIconProps} icon={icon}/>
}

export interface RingedToggleButtonProps extends Omit<ButtonProps, "variant" | "onChange"> {
    selected?: boolean;
    onChange?: (event: React.MouseEvent<HTMLElement>, value: boolean) => void;
}

export function RingedToggleButton(props: RingedToggleButtonProps) {
    const {children, selected, onChange, ...rest} = props;

    const onClick = useCallback((evt: React.MouseEvent<HTMLElement>) => {
        if (onChange) {
            onChange(evt, !selected)
        }
    }, [onChange, selected]);

    const cosmetics = selected
        ? {variant: "outlined", sx: {borderWidth: 2}} as const
        : {variant: "plain"} as const;

    return <Button {...cosmetics} onClick={onClick} {...rest}>
        {children}
    </Button>
}

// export function SortOrderToggleSwitch({value, dispatchValue}: ToggleSwitchProps<SortOrder>) {
//     return <ToggleButtonGroup value={value} onChange={(_, next) => {
//         if (next) {
//             dispatchValue(next);
//         }
//     }} sx={toggleSwitchSx}>
//         <Button sx={toggleSwitchButtonSx} value={SortOrder.ASC}>
//             <ToggleBarIcon icon={sortOrderIcons.ASC}/>
//         </Button>
//         <Button sx={toggleSwitchButtonSx} value={SortOrder.DESC}>
//             <ToggleBarIcon icon={sortOrderIcons.DESC}/>
//         </Button>
//     </ToggleButtonGroup>;
// }

// import {Dispatch, ReactNode, SetStateAction, useMemo, useState} from "react";
// import {cargoTimelineIcons} from "./CargoTimelineSwitch.tsx";
// import {FontAwesomeIcon, FontAwesomeIconProps} from "@fortawesome/react-fontawesome";
// import {editModeIcons} from "../forms.tsx";
// import {IconButton, Stack, ToggleButtonGroup, useTheme} from "@mui/joy";
// import {stableActiveIcons, trucksOrBundlesIcons} from "../routes/_auth/dispatch/overall.tsx";
// import {sortOrderIcons} from "../tqutil.tsx";
// import {scheduledTransferBucketIcons} from "../routes/_auth/driver/schedule.tsx";
// import {isString} from "lodash";
//
// export type ToggleIconMap<T extends string> = Record<T, FontAwesomeIconProps>;
//
// const toggleIconMap = {
//     cargoOrTimeline: cargoTimelineIcons,
//     trucksOrBundles: trucksOrBundlesIcons,
//     editMode: editModeIcons,
//     sortOrder: sortOrderIcons,
//     scheduledTransferBucket: scheduledTransferBucketIcons,
//     stableOrActive: stableActiveIcons,
// } as const;
//
// type toggleIconMapT = typeof toggleIconMap;
//
// type ToggleBarToggleName = keyof toggleIconMapT;
//
// type ToggleBarValue<T extends ToggleBarToggleName> = {
//     [Property in T]?: keyof toggleIconMapT[Property];
// };
//
// // function ToggleBarValueKeys<Name extends ToggleBarToggleName>(toggleName: Name) {
// //     return toggleIconMap[toggleName];
// // }
//
// export type ToggleBarProps<T extends ToggleBarToggleName> = {
//     value: ToggleBarValue<T>;
//     dispatchValue: Dispatch<SetStateAction<ToggleBarValue<T>>>;
// }
//
// // type ToggleBarToggleProps<Name extends ToggleBarToggleName> = {
// //     name: Name;
// //     value: ToggleBarValue<Name>;
// //     setValue: (next: ToggleBarValue<Name>) => void;
// // }
// //
// // type Entries<T> = {
// //     [K in keyof T]: [K, T[K]];
// // }[keyof T][];
//
// function ToggleBar<T extends ToggleBarToggleName>({value: compositeValue, dispatchValue}: ToggleBarProps<T>) {
//     const theme = useTheme();
//     const valueKeys = Object.keys(compositeValue) as (keyof ToggleBarValue<T>)[];
//     const controls = valueKeys.map((tn)  => {
//         const iconMap = toggleIconMap[tn];
//         const iconKeys = Object.keys(iconMap);
//         const value = compositeValue[tn];
//         if (!isString(value)) {
//             throw new Error(`${JSON.stringify(value)} unexpected`);
//         }
//         return <ToggleButtonGroup key={value} value={value} onChange={(_, next) => {
//             dispatchValue(prev => {
//                 const rv = {...prev, k: next};
//                 return rv;
//             })
//         }} sx={{px: 2, py: 1.6}}>
//             {iconKeys.map((rawv) => {
//                 const v = rawv as keyof typeof iconMap;
//                 if (!isString(v)) {
//                     throw new Error(`unexpected ${JSON.stringify(v)}`);
//                 }
//                 const xip = iconMap[v] as FontAwesomeIconProps;
//
//                 const iconProps: FontAwesomeIconProps = {size: "3x", ...xip};
//                 return <IconButton value={v} key={v}>
//                     <FontAwesomeIcon {...iconProps}/>
//                 </IconButton>
//             })}
//         </ToggleButtonGroup>
//     })
//     return <Stack direction="row" sx={{justifyContent: "space-between", bgcolor: theme.palette.primary[50], width: "100%"}}>
//         {[controls]}
//     </Stack>
// }
//
// export interface TogglePaneProps<T extends ToggleBarToggleName> {
//     defaultValue: ToggleBarValue<T>;
//     children: (value: ToggleBarValue<T>) => ReactNode;
// }
//
// export function TogglePane<T extends ToggleBarToggleName>({defaultValue, children}: TogglePaneProps<T>) {
//     const [state, setState] = useState<ToggleBarValue<T>>(defaultValue);
//     const renderedChildren = useMemo(() => children(state), [children, state]);
//
//     return <Stack direction="column">
//         <ToggleBar dispatchValue={setState} value={state}/>
//         {renderedChildren}
//     </Stack>
// }
//
// // const sampleToggleBar = <ToggleBar defaultValue={}/>
//
